<template>
  <PageBreadcrumbs :items="breadcrumbs" />
  <ContentCard>
    <template #subheader>
      <ReviewsAgencyReviewDetailsSubheader v-if="review" :review="review" @send-reply="openReplyDialog" />
    </template>
    <div v-if="review">
      <ReviewsAgencyReviewDetails
        :review="review"
        :agency-title="agency.title"
        @edit-reply="openReplyDialog"
        @delete-reply="handleDeleteReviewReply"
        @send-reminder="openReminderDialog"
      />
      <ReviewReplyDialog
        ref="reviewReplyModal"
        subject="agency"
        :universal-review-id="review.id"
        :review-reply="review.reviewReply"
        :entity-title="agency.title"
        :detail-page-url="detailPageUrl"
        :reviewer-first-name="reviewerFirstName"
        @closed="refetch"
        @submitted="onReplySubmitted"
      />
      <AgencySendReminderDialog
        ref="agencyReminderModal"
        :universal-review-id="review.id"
        :entity-id="agency.id"
        :reviewer-first-name="reviewerFirstName"
        @closed="refetch"
      />
      <ReviewReplyConfirmModal
        :show-confirmation-modal="showDeleteConfirmationModal"
        @canceled="onDeleteReviewReply(false)"
        @confirmed="onDeleteReviewReply(true)"
      />
    </div>
  </ContentCard>
</template>

<script setup lang="ts">
import { reviewsPaths } from '@ramp106/omrjs-routing'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import AgencySendReminderDialog from '@/components/ReviewsAgenciesManagements/Reviews/AgencySendReminderDialog.vue'
import ReviewsAgencyReviewDetails from '@/components/ReviewsAgenciesManagements/Reviews/ReviewsAgencyReviewDetails.vue'
import ReviewsAgencyReviewDetailsSubheader from '@/components/ReviewsAgenciesManagements/Reviews/ReviewsAgencyReviewDetailsSubheader.vue'
import ReviewReplyConfirmModal from '@/components/ReviewsCommon/ReviewReply/ReviewReplyConfirmModal.vue'
import ReviewReplyDialog from '@/components/ReviewsCommon/ReviewReply/ReviewReplyDialog.vue'
import { buildBreadcrumb } from '@/composables/breadcrumbs/buildBreadcrumb'
import type { AgencyData } from '@/gql/reviews'
import { useDeleteReviewReplyMutation, useReviewsAgencyUniversalReviewQuery } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { ReviewsRouteName } from '@/router/types'
import ContentCard from '@/ui/ContentCard.vue'

const props = defineProps<{
  agency: AgencyData
}>()

const route = useRoute()
const { t, locale } = useI18n()

const variables = computed(() => ({ id: route.params.reviewId as string }))
const { result, refetch } = useReviewsAgencyUniversalReviewQuery(variables, { clientId: 'reviews' })

const review = computed(() => result.value?.manage?.universalReview)

const reviewReplyModal = ref<InstanceType<typeof ReviewReplyDialog> | null>(null)
const agencyReminderModal = ref<InstanceType<typeof AgencySendReminderDialog> | null>(null)
const showDeleteConfirmationModal = ref(false)

const { mutate: mutateDeleteReviewReply } = useDeleteReviewReplyMutation({ clientId: 'reviews' })

const detailPageUrl = computed(() => reviewsPaths.agency({ locale: locale.value, slug: props.agency.slug }))

const reviewerFirstName = computed(() =>
  review.value && !review.value.anonymous ? review.value.reviewerName?.split(' ')[0] || undefined : undefined,
)

const reviewReplyPayload = computed(() =>
  review.value?.id
    ? { surface: 'editor' as const, entity_type: 'service' as const, entity_id: props.agency.id, review_id: review.value.id }
    : undefined,
)

function openReplyDialog() {
  if (reviewReplyPayload.value) {
    track('response_start', reviewReplyPayload.value)
  }
  reviewReplyModal.value?.open()
}

function onReplySubmitted() {
  if (reviewReplyPayload.value) {
    track('review_respond', reviewReplyPayload.value)
  }
}

function openReminderDialog() {
  agencyReminderModal.value?.open()
}

function handleDeleteReviewReply() {
  showDeleteConfirmationModal.value = true
}

async function onDeleteReviewReply(confirmed: boolean) {
  if (review.value?.reviewReply?.id && confirmed) {
    try {
      await mutateDeleteReviewReply({ id: review.value.reviewReply.id })
      refetch()
      showNotification(t('Reviews.reviewReply.delete.success'), 'success', 10000)
    } catch (_e) {
      showNotification(t('Reviews.reviewReply.delete.error'), 'error', 10000)
    }
  }
  showDeleteConfirmationModal.value = false
}

const breadcrumbs = computed(() => {
  const root = buildBreadcrumb(route, { name: ReviewsRouteName.ReviewsAgencies }, t('breadcrumbs.reviews.agencies'))
  const agencyReviews = buildBreadcrumb(
    route,
    { name: ReviewsRouteName.ReviewsAgencyReviewsView, params: { agencyId: props.agency.id } },
    t('breadcrumbs.reviews.agency.view.reviews', { agencyName: props.agency.title }),
  )
  const reviewDetails = {
    text: t('breadcrumbs.reviews.agency.view.reviewDetails', {
      authorName: review.value?.reviewerName || t('Reviews.agency.reviews.table.unknownAuthor'),
    }),
  }

  return [root, agencyReviews, reviewDetails]
})
</script>
